import { useState, useMemo } from "react";
import { Link } from "react-router-dom";

export type FunnelLead = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  event_type: string | null;
  event_date: string | null;
  status: string;
  source: string | null;
  created_at: string;
  converted_client_id: string | null;
};

export interface SalesFunnelData {
  leads: FunnelLead[];
}

type DateFilter = "this_month" | "last_30" | "all";

interface Props {
  data: SalesFunnelData;
}

const STATUS_META: {
  key: string;
  label: string;
  icon: string;
  color: string;
  bg: string;
  bar: string;
}[] = [
  { key: "new",       label: "New",       icon: "ri-user-received-line",   color: "text-[#a6853e]",  bg: "bg-[#C8A96E]/10",    bar: "bg-[#C8A96E]"      },
  { key: "contacted", label: "Contacted", icon: "ri-phone-line",           color: "text-teal-600",   bg: "bg-teal-100",        bar: "bg-teal-400"       },
  { key: "booked",    label: "Booked",    icon: "ri-calendar-check-line",  color: "text-emerald-600",bg: "bg-emerald-100",     bar: "bg-emerald-400"    },
  { key: "lost",      label: "Lost",      icon: "ri-close-circle-line",    color: "text-slate-400",  bg: "bg-slate-100",       bar: "bg-slate-300"      }, // eslint-disable-line
];

const BADGE_STYLES: Record<string, string> = {
  new:        "bg-[#C8A96E]/15 text-[#a6853e]",
  contacted:  "bg-teal-100 text-teal-700",
  booked:     "bg-emerald-100 text-emerald-700",
  lost:       "bg-slate-100 text-slate-400",
};

function filterByDate(leads: FunnelLead[], filter: DateFilter): FunnelLead[] {
  if (filter === "all") return leads;
  const now = Date.now();
  const cutoff = filter === "this_month"
    ? new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()
    : now - 30 * 24 * 60 * 60 * 1000;
  return leads.filter((l) => new Date(l.created_at).getTime() >= cutoff);
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DashboardSalesFunnel({ data }: Props) {
  const [filter, setFilter] = useState<DateFilter>("this_month");

  const filtered = useMemo(() => filterByDate(data.leads, filter), [data.leads, filter]);

  const total = filtered.length;

  const counts = useMemo(() => {
    const map: Record<string, number> = { new: 0, contacted: 0, booked: 0, lost: 0 };
    filtered.forEach((l) => {
      const s = l.status in map ? l.status : "new";
      map[s]++;
    });
    return map;
  }, [filtered]);

  const converted = useMemo(
    () => filtered.filter((l) => l.converted_client_id !== null).length,
    [filtered]
  );

  const conversionRate = useMemo(() => {
    if (total === 0) return 0;
    return Math.round((counts["booked"] / total) * 100);
  }, [counts, total]);

  const recentLeads = useMemo(
    () => [...filtered].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 6),
    [filtered]
  );

  const FILTER_LABELS: Record<DateFilter, string> = {
    this_month: "This Month",
    last_30:    "Last 30 days",
    all:        "All time",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
            <i className="ri-filter-line text-sm text-[#a6853e]"></i>
          </span>
          <div>
            <h2 className="text-[#0f172a] font-semibold text-sm">Sales Funnel</h2>
            <p className="text-slate-400 text-xs">Lead conversion overview</p>
          </div>
        </div>

        {/* Date filter */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          {(["this_month", "last_30", "all"] as DateFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                filter === f
                  ? "bg-white text-[#0f172a] shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Funnel Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {STATUS_META.map((s) => {
            const count = counts[s.key] ?? 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={s.key} className="bg-slate-50 rounded-xl p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${s.bg}`}>
                    <i className={`${s.icon} text-sm ${s.color}`}></i>
                  </span>
                  <span className="text-slate-400 text-xs">{pct}%</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f172a] leading-none">{count}</p>
                  <p className="text-slate-500 text-xs mt-0.5">{s.label}</p>
                </div>
                {/* Bar */}
                <div className="h-1 w-full bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${s.bar} rounded-full transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Conversion Summary Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Total Leads */}
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 shrink-0">
              <i className="ri-team-line text-sm text-[#a6853e]"></i>
            </span>
            <div>
              <p className="text-xl font-bold text-[#0f172a] leading-none">{total}</p>
              <p className="text-slate-500 text-xs mt-0.5">Total Leads</p>
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-xl">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 shrink-0">
              <i className="ri-percent-line text-sm text-emerald-600"></i>
            </span>
            <div>
              <p className="text-xl font-bold text-emerald-700 leading-none">{conversionRate}%</p>
              <p className="text-emerald-600 text-xs mt-0.5">Conversion Rate</p>
            </div>
          </div>

          {/* Fully Converted */}
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100 shrink-0">
              <i className="ri-user-follow-line text-sm text-teal-600"></i>
            </span>
            <div>
              <p className="text-xl font-bold text-[#0f172a] leading-none">{converted}</p>
              <p className="text-slate-500 text-xs mt-0.5">Converted to Client</p>
            </div>
          </div>
        </div>

        {/* Recent Leads List */}
        {recentLeads.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Recent Leads</p>
              <Link
                to="/admin/leads"
                className="text-[#C8A96E] hover:text-[#a6853e] text-xs transition-colors cursor-pointer whitespace-nowrap"
              >
                View all →
              </Link>
            </div>
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
              {recentLeads.map((lead) => (
                <Link
                  key={lead.id}
                  to={`/admin/leads/${lead.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  {/* Avatar */}
                  <div className="w-7 h-7 rounded-full bg-[#C8A96E]/15 flex items-center justify-center shrink-0">
                    <span className="text-[#a6853e] text-xs font-bold">
                      {lead.name.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-[#0f172a] text-xs font-medium truncate">{lead.name}</p>
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize ${
                          BADGE_STYLES[lead.status] ?? "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {lead.status}
                      </span>
                      {lead.converted_client_id && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700">
                          client
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {lead.event_type && (
                        <span className="text-slate-400 text-[11px]">{lead.event_type}</span>
                      )}
                      {lead.source && (
                        <span className="text-slate-300 text-[11px]">· {lead.source}</span>
                      )}
                    </div>
                  </div>

                  {/* Time */}
                  <span className="text-slate-400 text-xs whitespace-nowrap shrink-0">{timeAgo(lead.created_at)}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {total === 0 && (
          <div className="flex flex-col items-center gap-2 py-8">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-slate-300">
              <i className="ri-filter-line text-lg"></i>
            </span>
            <p className="text-slate-400 text-sm">No leads for this period</p>
            <p className="text-slate-300 text-xs">Try switching to "All time" to see historical data</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 pb-4">
        <Link
          to="/admin/leads"
          className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-medium text-slate-500 hover:text-[#0f172a] hover:bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          Manage all leads
        </Link>
      </div>
    </div>
  );
}